// components/CreateCriteriaForm/index.tsx
import React, { useState, useEffect } from 'react';
import {
    Flex,
    Input,
    Button,
    Card,
    Form,
    InputNumber,
    notification,
    FormInstance,
    Spin
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import TotalWeight from './TotalWeight';
import {
    createCandidateCriteria,
    editCandidateCriteria,
    getCandidateCriteriaDetail
} from 'api/search.api';

interface FormQuestion {
    key: number;
    text: string;
    weight?: number;
    id?: number;
}

interface CreateCriteriaFormProps {
    editingTemplateId?: number | null;
    onSuccess?: (templateId: number, templateName: string, questions: string[]) => void;
    onCancel?: () => void;
    onClose?: () => void;
    form?: FormInstance;
    maxQuestions?: number;
    showCancel?: boolean;
    onTemplateLoad?: (template: any) => void;
}

const CreateCriteriaForm: React.FC<CreateCriteriaFormProps> = ({
    editingTemplateId = null,
    onSuccess,
    onCancel,
    onClose,
    form,
    maxQuestions = 8,
    showCancel = true,
    onTemplateLoad
}) => {
    const [templateName, setTemplateName] = useState('');
    const [formQuestions, setFormQuestions] = useState<FormQuestion[]>([{ key: 0, text: '' }]);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [totalPercentage, setTotalPercentage] = useState(0);
    const [formInstance] = Form.useForm(form);

    // Load template data if editing
    useEffect(() => {
        if (editingTemplateId) {
            loadTemplateForEdit(editingTemplateId);
        }
    }, [editingTemplateId]);

    // Calculate total percentage whenever formQuestions change
    useEffect(() => {
        const total = formQuestions.reduce((sum, question) => sum + (question.weight || 0), 0);
        setTotalPercentage(total);
    }, [formQuestions]);

    // Load template for editing
    const loadTemplateForEdit = async (templateId: number) => {
        setLoading(true);
        try {
            const template = await getCandidateCriteriaDetail(templateId);

            // Map API questions to form questions
            const questions: FormQuestion[] = template.questions.map((question: any, index: number) => ({
                key: index,
                text: question.question,
                weight: question.weight || 0,
                id: question.id
            }));

            setFormQuestions(questions);
            setTemplateName(template.name);

            if (onTemplateLoad) {
                onTemplateLoad(template);
            }

            notification.success({
                message: 'Success',
                description: `Loaded template: ${template.name}`,
            });
        } catch (error) {
            console.error('Error loading template for edit:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to load template for editing',
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle form input changes
    const handleFormInputChange = (key: number, value: string) => {
        setFormQuestions((prev) =>
            prev.map((q) => (q.key === key ? { ...q, text: value } : q))
        );
    };

    // Handle percentage/weight changes
    const handlePercentageChange = (key: number, weight: number) => {
        const validWeight = Math.max(0, Math.min(100, weight));

        setFormQuestions((prev) => {
            const currentTotal = prev.reduce((sum, q) => sum + (q.weight || 0), 0);
            const currentQuestionWeight = prev.find(q => q.key === key)?.weight || 0;
            const weightDifference = validWeight - currentQuestionWeight;

            // If adding this weight would exceed 100%, adjust other weights
            if (currentTotal + weightDifference > 100) {
                const availableWeight = 100 - (currentTotal - currentQuestionWeight);
                return prev.map((q) =>
                    q.key === key
                        ? { ...q, weight: availableWeight }
                        : q
                );
            }

            return prev.map((q) =>
                q.key === key ? { ...q, weight: validWeight } : q
            );
        });
    };

    // Add new question field
    const handleAddFormQuestion = () => {
        const hasEmptyQuestion = formQuestions.some((q) => q.text.trim() === '');
        if (hasEmptyQuestion) {
            notification.error({
                message: 'Error',
                description: 'Please fill in the existing question before adding a new one.',
            });
            return;
        }

        if (formQuestions.length >= maxQuestions) {
            notification.warning({
                message: 'Limit Reached',
                description: `You can only add up to ${maxQuestions} questions.`,
            });
            return;
        }

        const newKey = Math.max(...formQuestions.map(q => q.key), 0) + 1;
        setFormQuestions([
            ...formQuestions,
            { key: newKey, text: '', weight: 0 }
        ]);
    };

    // Remove question field
    const handleRemoveFormQuestion = (key: number) => {
        if (formQuestions.length <= 1) {
            notification.warning({
                message: 'Warning',
                description: 'At least one question is required.',
            });
            return;
        }
        setFormQuestions(formQuestions.filter((q) => q.key !== key));
    };

    // Validate form before submission
    const validateForm = (): boolean => {
        if (!templateName.trim()) {
            notification.error({
                message: 'Error',
                description: 'Please enter template name!',
            });
            return false;
        }

        if (templateName.length < 3) {
            notification.error({
                message: 'Error',
                description: 'Template name must be at least 3 characters!',
            });
            return false;
        }

        const nonEmptyQuestions = formQuestions.filter(q => q.text.trim() !== '');
        if (nonEmptyQuestions.length === 0) {
            notification.error({
                message: 'Error',
                description: 'Please add at least one criteria!',
            });
            return false;
        }

        if (totalPercentage !== 100) {
            notification.error({
                message: 'Validation Error',
                description: `The sum of weights must be 100. Current sum: ${totalPercentage}`,
            });
            return false;
        }

        return true;
    };

    // Create or update template
    const handleCreateTemplate = async () => {
        if (!validateForm()) {
            return;
        }

        setSubmitLoading(true);

        try {
            const nonEmptyQuestions = formQuestions.filter(q => q.text.trim() !== '');

            const payload = {
                name: templateName,
                questions: nonEmptyQuestions.map((q, index) => ({
                    text: q.text,
                    order: index,
                    weight: q.weight || 0,
                    ...(q.id && { id: q.id }) // Include id for existing questions during edit
                }))
            };

            let response;
            if (editingTemplateId) {
                // Update existing template
                response = await editCandidateCriteria(editingTemplateId, payload);
                notification.success({
                    message: 'Success',
                    description: 'Template updated successfully!',
                });
            } else {
                // Create new template
                response = await createCandidateCriteria(payload);
                notification.success({
                    message: 'Success',
                    description: 'Template created successfully!',
                });
            }

            // Get the template ID
            const templateId = editingTemplateId || response.question_set_id;

            // Prepare questions array for callback
            const questionsArray = nonEmptyQuestions.map(q => q.text);
            
            // Call success callback if provided
            if (onSuccess && templateId) {
                onSuccess(templateId, templateName, questionsArray);
            }

            // Reset form if not editing
            if (!editingTemplateId) {
                resetForm();
            }

        } catch (error: any) {
            console.error('Template operation error:', error);
            notification.error({
                message: 'Error',
                description: error?.message || `An error occurred while ${editingTemplateId ? 'updating' : 'creating'} the template.`,
            });
        } finally {
            setSubmitLoading(false);
        }
    };

    // Reset form to initial state
    const resetForm = () => {
        setTemplateName('');
        setFormQuestions([{ key: 0, text: '', weight: 0 }]);
        setTotalPercentage(0);
    };

    // Handle cancel
    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
        resetForm();
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center p-8">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <>
            <Flex vertical gap={16}>
                {/* Template Name */}
                <Flex gap={16} align="center">
                    <span className="w-32 font-medium">Template Name</span>
                    <Input
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="Enter template name"
                        size="large"
                        style={{ flex: 1 }}
                        maxLength={100}
                    />
                </Flex>

                {/* Questions List */}
                {formQuestions.map((question, index) => (
                    <Card key={question.key} className="shadow-sm">
                        <Flex gap={16} align="start">
                            <Flex className="w-32 gap-1 mt-2">
                                <span className="font-medium">{`Criteria #${index + 1}`}</span>
                                <span className="text-red-500">*</span>
                            </Flex>
                            <Flex className="w-full" gap={8} vertical>
                                <Input
                                    placeholder="Enter criteria"
                                    size="large"
                                    value={question.text}
                                    onChange={(e) => handleFormInputChange(question.key, e.target.value)}
                                    maxLength={500}
                                    showCount
                                />
                            </Flex>

                            {formQuestions.length > 1 && (
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleRemoveFormQuestion(question.key)}
                                    size="large"
                                    title="Remove criteria"
                                />
                            )}
                        </Flex>
                        <Flex gap={12} align="start" className="mt-3">
                            <div className="flex-1"></div>
                            <div className="min-w-[140px] p-2 bg-gray-50 rounded-md">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-600">
                                        Weight:
                                    </span>
                                    <Input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={question.weight || 0}
                                        onChange={(e) => handlePercentageChange(question.key, parseInt(e.target.value) || 0)}
                                        placeholder="0"
                                        className="w-16"
                                        suffix="%"
                                        size="small"
                                    />
                                </div>
                                {/* <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-600">
                    Weight:
                  </span>
                  <InputNumber
                    min={0}
                    max={100}
                    value={question.weight || 0}
                    onChange={(value) => handlePercentageChange(question.key, value || 0)}
                    placeholder="0"
                    className="w-16"
                    addonAfter="%"
                    size="small"
                    parser={(value) => Math.min(100, Math.max(0, parseInt(value?.replace('%', '') || '0')))}
                    formatter={(value) => `${value}%`}
                  />
                </div> */}
                            </div>
                        </Flex>
                    </Card>
                ))}

                {/* Total Weight Display */}
                {formQuestions.length > 0 && (
                    <div className="w-64 ml-auto">
                        <TotalWeight percent={totalPercentage} />
                    </div>
                )}

                {/* Add Question Button */}
                <Card className="border-dashed mb-5">
                    <Flex justify="center" align="center">
                        <Flex
                            onClick={formQuestions.length < maxQuestions ? handleAddFormQuestion : undefined}
                            className={`px-8 rounded-xl items-center ${formQuestions.length >= maxQuestions ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-gray-50"}`}
                            align="center"
                            gap={12}
                        >
                            <div className="w-10 h-10 flex justify-center items-center rounded-full border">
                                <PlusOutlined />
                            </div>
                            <Flex vertical className="text-center">
                                <span className="font-semibold text-gray-600">Add Your Criteria</span>
                                <span className="text-gray-400 text-sm">ex: years of experience</span>
                            </Flex>
                        </Flex>
                    </Flex>

                    {formQuestions.length >= maxQuestions && (
                        <div className="text-red-500 text-sm mt-2 text-center">
                            You reached your maximum limit to add criteria.
                        </div>
                    )}
                </Card>
            </Flex>

            {/* Action Buttons */}
            <Flex gap={8} className="justify-end">
                {showCancel && (
                    <Button
                        onClick={handleCancel}
                        size="large"
                        disabled={submitLoading}
                    >
                        Cancel
                    </Button>
                )}
                <Button
                    type="primary"
                    onClick={handleCreateTemplate}
                    loading={submitLoading}
                    size="large"
                    disabled={!templateName.trim() || formQuestions.some(q => !q.text.trim()) || totalPercentage !== 100}
                >
                    {editingTemplateId ? "Update Template" : "Save Template"}
                </Button>
            </Flex>
        </>
    );
};

export default CreateCriteriaForm;